/**
 * Один разбор плейсхолдеров на весь фронт.
 *
 * Токен `{{ contact.X }}` ищут три места: панель проверки текста, чипы в
 * редакторе письма и защита плейсхолдеров в поле SMS. Разъехавшиеся копии
 * выражения дают худший вид ошибки — редактор считает токен целым, а проверка
 * текста тот же токен не видит, и оба молчат.
 *
 * Пробелы вокруг имени допускают оба движка подстановки — Brevo в письме и
 * Twig в SMS; перевод строки внутри скобок не допускает ни один.
 */
const PATTERN = String.raw`\{\{\s*([^{}\n]+?)\s*\}\}`;

/**
 * Свежее выражение на каждый вызов.
 *
 * Глобальный поиск помнит `lastIndex`, и общая на всех константа однажды
 * начинает пропускать первый токен в тексте — ровно один раз через раз.
 */
export const placeholderPattern = (): RegExp => new RegExp(PATTERN, "g");

/** Место токена в строке — с ним работает защита от порчи плейсхолдера. */
export type PlaceholderRange = {
  start: number;
  end: number;
  token: string;
};

export const placeholderRanges = (text: string): PlaceholderRange[] => {
  const pattern = placeholderPattern();
  const ranges: PlaceholderRange[] = [];

  let match = pattern.exec(text);

  while (match !== null) {
    ranges.push({
      start: match.index,
      end: match.index + match[0].length,
      token: match[1],
    });

    match = pattern.exec(text);
  }

  return ranges;
};

/** Токен, внутрь которого попадает позиция; границы токена — уже снаружи. */
export const placeholderAround = (
  text: string,
  position: number,
): PlaceholderRange | null =>
  placeholderRanges(text).find(
    (range) => position > range.start && position < range.end,
  ) ?? null;

/** Токен, накрывающий символ с этой позиции. */
export const placeholderAt = (
  text: string,
  position: number,
): PlaceholderRange | null =>
  placeholderRanges(text).find(
    (range) => position >= range.start && position < range.end,
  ) ?? null;

/**
 * Раздвигает выделение до целых токенов.
 *
 * Иначе половина плейсхолдера остаётся в тексте и выглядит как обычные буквы:
 * `{{ contact.EMPF_PRO` ничем не подсвечивается и подставится как есть.
 */
export const expandToPlaceholders = (
  text: string,
  start: number,
  end: number,
): { start: number; end: number } => {
  const touched = placeholderRanges(text).filter(
    (range) => range.start < end && range.end > start,
  );

  if (touched.length === 0) {
    return { start, end };
  }

  return {
    start: Math.min(start, ...touched.map((range) => range.start)),
    end: Math.max(end, ...touched.map((range) => range.end)),
  };
};

/**
 * Токен, на который указывает позиция, — начало важнее конца.
 *
 * У двух плейсхолдеров подряд конец первого и начало второго — одна и та же
 * позиция. Пока брали «первый подходящий», щелчок по второму чипу выделял
 * первый: тот включал эту позицию своим концом. Поэтому конец проверяется
 * последним и годится лишь тогда, когда на этой позиции больше ничего нет.
 */
export const placeholderForPosition = (
  text: string,
  position: number,
): PlaceholderRange | null => {
  const ranges = placeholderRanges(text);

  return (
    ranges.find((range) => position === range.start) ??
    ranges.find((range) => position > range.start && position < range.end) ??
    ranges.find((range) => position === range.end) ??
    null
  );
};
